import { useQuery } from '@apollo/client'
import { Grid2 as Grid, Typography } from '@mui/material'
import { isNil } from 'lodash'

import StatWithProgress from '@/components/reports/StatWithProgress'
import { useRefresh } from '@/contexts/RefreshContext'
import {
  GET_POS_REVENUE_AND_FUEL_STATS,
  GetPosRevenueAndFuelStatsQuery,
  GetPosRevenueAndFuelStatsQueryVariables,
} from '@/graphql'
import useT from '@/i18n/useT'
import Stats from '@/pages/dashboards/Default/Stats'
import { DashboardStatsHelpers } from '@/utils/dashboardStats.helpers'
import { DateHelpers } from '@/utils/date.helpers'
import { computePercentChange, formatAmount } from '@/utils/statCards'

import { SalesCategorySetting } from './SalesCategorySettings'

type Props = {
  startDate: string
  endDate: string
  storeIds?: string[]
  insideCategories?: string[]
  outsideCategories?: string[]
  fuelCategories?: string[]
  onSettingsClick: (setting: SalesCategorySetting) => void
}

const STAT_NAME_TO_SETTING: { [key: string]: SalesCategorySetting } = {
  inside_sales: 'inside_sales_categories',
  outside_sales: 'outside_sales_categories',
  fuel_volume: 'fuel_sales_categories',
}

export const RevAndFuelStatCards: React.FC<Props> = props => {
  const {
    storeIds,
    startDate,
    endDate,
    insideCategories,
    outsideCategories,
    fuelCategories,
    onSettingsClick,
  } = props
  const { t } = useT()

  const { data, loading, refetch } = useQuery<
    GetPosRevenueAndFuelStatsQuery,
    GetPosRevenueAndFuelStatsQueryVariables
  >(GET_POS_REVENUE_AND_FUEL_STATS, {
    notifyOnNetworkStatusChange: true,
    variables: {
      filters: {
        storeIds,
        startDate,
        endDate,
        insideCategories,
        outsideCategories,
        fuelCategories,
      },
      pagination: { limit: 1, offset: 0 },
    },
  })

  useRefresh(refetch)

  return (
    <Grid container spacing={3} mt={3}>
      {DashboardStatsHelpers.transformRevenueDetailsPage(
        data?.posRevenueAndFuelStats.results[0],
        t,
        startDate,
        endDate,
      ).map(stat => {
        const change =
          isNil(stat.value) || DateHelpers.isSingleDayToday(startDate, endDate)
            ? null
            : computePercentChange(stat)
        const isPercentage = stat.valueType === 'PERCENTAGE'
        return (
          <Grid
            key={stat.label}
            size={{ xs: 12, sm: 6, md: 4, lg: 4, xl: 'grow' }}
            sx={{ display: 'flex', flexDirection: 'column' }}
          >
            {
              <Stats
                title={stat.label}
                onIconClick={
                  STAT_NAME_TO_SETTING[stat.name]
                    ? () => onSettingsClick(STAT_NAME_TO_SETTING[stat.name])
                    : undefined
                }
                amount={
                  isNil(stat.value) ? (
                    <Typography variant="h6" gutterBottom>
                      {t('homeDashboard.stats.noData')}
                    </Typography>
                  ) : isPercentage ? (
                    <StatWithProgress value={stat.value || 0} />
                  ) : (
                    formatAmount(stat)
                  )
                }
                changeTooltip={stat.changeTooltip}
                change={change}
                changeTitle={stat.changeTitle}
                loading={loading}
              />
            }
          </Grid>
        )
      })}
    </Grid>
  )
}
